// Step 2: the parameter set (rooms, view protection, wet rooms, regulations, priorities).
import { useState } from "react";
import type { Facade, ParameterSet, RoomDefinition, RoomType, Rule } from "@unitly/contracts";

export const ROOM_TYPES: { id: RoomType; label: string; fill: string }[] = [
  { id: "living_kitchen", label: "Living + kitchen", fill: "var(--room-living)" },
  { id: "bedroom", label: "Bedroom", fill: "var(--room-bed)" },
  { id: "bathroom", label: "Bathroom", fill: "var(--room-bath)" },
  { id: "guest_wc", label: "Guest WC", fill: "var(--room-bath)" },
  { id: "laundry", label: "Laundry", fill: "var(--room-bath)" },
  { id: "hall", label: "Hall", fill: "var(--room-circ)" },
  { id: "corridor", label: "Corridor", fill: "var(--room-circ)" },
  { id: "terrace", label: "Terrace", fill: "var(--room-terrace)" },
];
const FACADES: Facade[] = ["N", "E", "S", "W"];
const typeOf = (t: RoomType) => ROOM_TYPES.find((x) => x.id === t)!;

export function ParametersPanel({ params, setParams, dirty, saving, savedAt, onSave, onReset, error, windows }: {
  params: ParameterSet;
  setParams: (p: ParameterSet) => void;
  dirty: boolean;
  saving: boolean;
  savedAt: string | null;
  onSave: () => void;
  onReset: () => void;
  error: string | null;
  windows: number;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const update = (fn: (p: ParameterSet) => void) => {
    const next = structuredClone(params);
    fn(next);
    setParams(next);
  };
  const totalMin = params.rooms.reduce((a, r) => a + r.min_area_m2, 0);

  return (
    <aside className="panel" aria-label="Parameters and brief">
      <div className="panel-head">
        <span className="panel-title">Parameters</span>
        <span className="panel-sub">{params.name} · rooms need at least {totalMin.toFixed(1)} m²</span>
      </div>
      <div className="panel-body">
        {error && <div className="section"><div className="notice error" role="alert">{error}</div></div>}

        <div className="section" style={{ paddingBottom: 8 }}>
          <span className="section-title">Rooms <span className="hint">per unit</span></span>
        </div>
        {params.rooms.map((r, i) => (
          <RoomRow key={r.id} room={r} all={params.rooms} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)}
            onChange={(next) => update((p) => { p.rooms[i] = next; })}
            onRemove={() => update((p) => { p.rooms.splice(i, 1); })} />
        ))}
        <div className="section" style={{ flexDirection: "row" }}>
          <button className="btn small" onClick={() => {
            const id = uniqueId(params.rooms, "room");
            update((p) => { p.rooms.push({ id, label: "New room", type: "bedroom", min_area_m2: 8, max_area_m2: null, min_width_m: 2.4, needs_window: true, preferred_facades: [], access: { must_open_to: ["corridor"], must_not_open_to: [] } }); });
            setOpen(id);
          }}>Add room</button>
        </div>

        <div className="section">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="section-title" style={{ flex: 1 }}>View protection</span>
            <Toggle checked={params.view_protection.enabled} label="On" onChange={(v) => update((p) => { p.view_protection.enabled = v; })} />
          </div>
          <div style={{ display: "flex", fontSize: 13 }}>
            <span className="muted" style={{ flex: 1 }}>Clear zone in front of bedroom windows</span>
            <span style={{ fontWeight: 600 }}>{params.view_protection.depth_m.toFixed(1)} m</span>
          </div>
          <input type="range" min={0} max={6} step={0.1} value={params.view_protection.depth_m} disabled={!params.view_protection.enabled}
            aria-label="Clear zone depth" onChange={(e) => update((p) => { p.view_protection.depth_m = Number(e.target.value); })} />
          <span className="faint" style={{ fontSize: 11.5 }}>
            Shown hatched on the plan in front of all {windows} windows; the solver keeps it free in front of the windows it gives to bedrooms.
          </span>
        </div>

        <div className="section">
          <span className="section-title">Wet rooms</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center" }}>
            <span className="muted">Max distance from bathrooms, WC, laundry and kitchen to a shaft</span>
            <NumberInput value={params.wet_rooms.max_distance_to_shaft_m} unit="m" step={0.1} min={0} max={30}
              onChange={(v) => update((p) => { p.wet_rooms.max_distance_to_shaft_m = v; })} label="Max distance to shaft" />
          </div>
        </div>

        <div className="section" style={{ paddingBottom: 8 }}>
          <span className="section-title">Regulations <span className="hint">Malaga PGOU + CTE</span></span>
          <span className="faint" style={{ fontSize: 11.5 }}>Values as briefed. Tick a rule once an architect has confirmed it.</span>
        </div>
        {params.regulations.rules.map((rule, i) => (
          <RuleRow key={rule.id} rule={rule} onChange={(next) => update((p) => { p.regulations.rules[i] = next; })} />
        ))}

        <div className="section" style={{ borderBottom: "none" }}>
          <span className="section-title">Priorities <span className="hint">drag to reorder, most important first</span></span>
          <Priorities items={params.priorities} onChange={(items) => update((p) => { p.priorities = items; })} />
        </div>
      </div>
      <div className="panel-foot">
        <button className="btn" onClick={onReset} disabled={saving}>Reset to preset</button>
        <span className="faint" style={{ fontSize: 11.5, flex: 1, textAlign: "right" }}>
          {dirty ? "Unsaved changes" : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Preset, not saved yet"}
        </span>
        <button className="btn primary" onClick={onSave} disabled={saving || (!dirty && !!savedAt)}>{saving ? "Saving" : "Save parameters"}</button>
      </div>
    </aside>
  );
}

function uniqueId(rooms: RoomDefinition[], base: string) {
  let i = rooms.length + 1;
  while (rooms.some((r) => r.id === `${base}_${i}`)) i++;
  return `${base}_${i}`;
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button className="toggle" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}>
      <span className="track"><span className="knob" /></span>
    </button>
  );
}

function NumberInput({ value, onChange, unit, step, min, max, label }: {
  value: number; onChange: (v: number) => void; unit: string; step: number; min: number; max: number; label: string;
}) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  return (
    <span className="unit-input">
      <input type="number" inputMode="decimal" aria-label={label} step={step} min={min} max={max}
        value={focused ? text : String(value)}
        onFocus={() => { setText(String(value)); setFocused(true); }}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value);
          const v = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(v)) onChange(Math.min(max, Math.max(min, v)));
        }} />
      <span>{unit}</span>
    </span>
  );
}

function RoomRow({ room, all, open, onToggle, onChange, onRemove }: {
  room: RoomDefinition; all: RoomDefinition[]; open: boolean; onToggle: () => void;
  onChange: (r: RoomDefinition) => void; onRemove: () => void;
}) {
  const set = (fn: (r: RoomDefinition) => void) => {
    const next = structuredClone(room);
    fn(next);
    onChange(next);
  };
  const targets = ["entrance", "corridor", ...all.filter((r) => r.id !== room.id && r.id !== "corridor").map((r) => r.id)];
  const labelOf = (id: string) => id === "entrance" ? "Entrance" : id === "corridor" ? "Corridor" : all.find((r) => r.id === id)?.label ?? id;
  const toggleIn = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  return (
    <div className="room">
      <button className="room-head" onClick={onToggle} aria-expanded={open}>
        <span className="fill" style={{ background: typeOf(room.type).fill, outline: "1px solid var(--n-200)" }} />
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{room.label}</span>
          <span className="faint" style={{ fontSize: 11.5 }}>{typeOf(room.type).label}{room.needs_window ? " · window" : ""}</span>
        </span>
        <span style={{ fontSize: 12 }}>{"≥"} {room.min_area_m2.toFixed(1)} m²</span>
        <span className="faint" aria-hidden>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="room-body">
          <label className="field wide">Name
            <input className="input" value={room.label} onChange={(e) => set((r) => { r.label = e.target.value; })} />
          </label>
          <label className="field">Type
            <select className="select" value={room.type} onChange={(e) => set((r) => { r.type = e.target.value as RoomType; })}>
              {ROOM_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="field">Needs window
            <span style={{ paddingTop: 6 }}>
              <button className="toggle" role="switch" aria-checked={room.needs_window} onClick={() => set((r) => { r.needs_window = !r.needs_window; })}>
                <span className="track"><span className="knob" /></span>{room.needs_window ? "Yes" : "No"}
              </button>
            </span>
          </label>
          <label className="field">Min area
            <NumberInput value={room.min_area_m2} unit="m²" step={0.5} min={0} max={500} label="Min area" onChange={(v) => set((r) => { r.min_area_m2 = v; })} />
          </label>
          <label className="field">Min width
            <NumberInput value={room.min_width_m} unit="m" step={0.05} min={0} max={20} label="Min width" onChange={(v) => set((r) => { r.min_width_m = v; })} />
          </label>
          <div className="field wide">Preferred facades
            <div className="chips">
              {FACADES.map((f) => (
                <button key={f} className={`chip${room.preferred_facades.includes(f) ? " on" : ""}`} aria-pressed={room.preferred_facades.includes(f)}
                  onClick={() => set((r) => { r.preferred_facades = toggleIn(r.preferred_facades, f) as Facade[]; })}>{f}</button>
              ))}
              {!room.preferred_facades.length && <span className="faint" style={{ fontSize: 11.5, alignSelf: "center" }}>any</span>}
            </div>
          </div>
          <div className="field wide">Must open to
            <div className="chips">
              {targets.map((t) => (
                <button key={t} className={`chip${room.access.must_open_to.includes(t) ? " on" : ""}`} aria-pressed={room.access.must_open_to.includes(t)}
                  onClick={() => set((r) => { r.access.must_open_to = toggleIn(r.access.must_open_to, t); r.access.must_not_open_to = r.access.must_not_open_to.filter((x) => x !== t); })}>{labelOf(t)}</button>
              ))}
            </div>
          </div>
          <div className="field wide">Must not open to
            <div className="chips">
              {targets.map((t) => (
                <button key={t} className={`chip${room.access.must_not_open_to.includes(t) ? " on" : ""}`} aria-pressed={room.access.must_not_open_to.includes(t)}
                  onClick={() => set((r) => { r.access.must_not_open_to = toggleIn(r.access.must_not_open_to, t); r.access.must_open_to = r.access.must_open_to.filter((x) => x !== t); })}>{labelOf(t)}</button>
              ))}
            </div>
          </div>
          {room.notes && <span className="faint wide" style={{ fontSize: 11.5 }}>{room.notes}</span>}
          <div className="wide" style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn small" style={{ color: "var(--error)" }} onClick={onRemove}>Remove room</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RuleRow({ rule, onChange }: { rule: Rule; onChange: (r: Rule) => void }) {
  const unit = rule.unit === "m2" ? "m²" : rule.unit === "ratio" ? "" : rule.unit === "count" ? "" : rule.unit;
  return (
    <div className="rule" style={{ opacity: rule.enabled ? 1 : 0.55 }}>
      <button className="toggle" role="switch" aria-checked={rule.enabled} aria-label={`Apply ${rule.title}`} onClick={() => onChange({ ...rule, enabled: !rule.enabled })}>
        <span className="track"><span className="knob" /></span>
      </button>
      <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 12.5 }}>{rule.title}</span>
        <span className="meta">
          <span>{rule.article}</span>
          <label>
            <input type="checkbox" checked={rule.verified_by_architect} onChange={(e) => onChange({ ...rule, verified_by_architect: e.target.checked })} />
            Verified by architect
          </label>
          {!rule.verified_by_architect && <span className="badge warn" style={{ padding: "1px 4px", fontSize: 9 }}>UNVERIFIED</span>}
        </span>
      </span>
      {rule.kind === "independent_bathroom_access" ? (
        <span className="faint" style={{ fontSize: 12, textAlign: "right" }}>rule</span>
      ) : (
        <NumberInput value={rule.value} unit={rule.unit === "ratio" ? "ratio" : unit} step={rule.unit === "ratio" ? 0.01 : 0.05} min={0} max={1000}
          label={rule.title} onChange={(v) => onChange({ ...rule, value: v })} />
      )}
    </div>
  );
}

function Priorities({ items, onChange }: { items: ParameterSet["priorities"]; onChange: (i: ParameterSet["priorities"]) => void }) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= items.length) return;
    const next = [...items];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    onChange(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} role="list">
      {items.map((p, i) => (
        <div key={p.id} role="listitem" draggable
          className={`prio${dragging === i ? " dragging" : ""}${over === i && dragging !== i ? " over" : ""}`}
          onDragStart={(e) => { setDragging(i); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); }}
          onDragOver={(e) => { e.preventDefault(); setOver(i); }}
          onDragLeave={() => setOver(null)}
          onDrop={(e) => { e.preventDefault(); move(Number(e.dataTransfer.getData("text/plain")), i); setDragging(null); setOver(null); }}
          onDragEnd={() => { setDragging(null); setOver(null); }}>
          <span className="grip" aria-hidden>{"≡"}</span>
          <span className="n">{i + 1}</span>
          <span>{p.label}</span>
          <span className="moves">
            <button aria-label={`Move ${p.label} up`} disabled={i === 0} onClick={() => move(i, i - 1)}>{"↑"}</button>
            <button aria-label={`Move ${p.label} down`} disabled={i === items.length - 1} onClick={() => move(i, i + 1)}>{"↓"}</button>
          </span>
        </div>
      ))}
    </div>
  );
}
